import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Badge,
  Progress,
  Loader,
  Center,
  SimpleGrid,
  Box,
  ThemeIcon,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Users, Briefcase } from 'react-feather';
import { coachPortalApi, type CoachCohort } from '../../services/api/coachPortal';
import { COHORT_STATUS_CONFIG } from '../../config/statusConfig';
import { formatDateOnlyRange } from '../../utils/formatters';
import classes from './CoachCohortsPage.module.css';

// Cohort card component
function CohortCard({ cohort, onClick }: { cohort: CoachCohort; onClick: () => void }) {
  const isAwaitingContract = cohort.contract_status !== 'signed';
  const statusConfig = COHORT_STATUS_CONFIG[cohort.status as keyof typeof COHORT_STATUS_CONFIG];
  const sessionProgress =
    cohort.session_count > 0
      ? Math.round((cohort.sessions_completed / cohort.session_count) * 100)
      : 0;

  return (
    <Card
      withBorder
      padding="lg"
      radius="md"
      className={`${classes.cohortCard} ${isAwaitingContract ? classes.cohortCardDisabled : ''}`}
      onClick={isAwaitingContract ? undefined : onClick}
    >
      <Stack gap="md">
        {/* Header with status badge */}
        <Group justify="space-between" wrap="nowrap">
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text size="lg" fw={600} truncate>
              {cohort.name}
            </Text>
            <Text size="sm" c="dimmed" truncate>
              {cohort.partner_name || 'No Partner'} • {cohort.code}
            </Text>
          </Box>
          {isAwaitingContract ? (
            <Badge color="warningAmber" variant="light">
              Awaiting Contract
            </Badge>
          ) : (
            <Badge color={statusConfig?.color || 'gray'} variant={statusConfig?.variant ?? 'light'}>
              {statusConfig?.label || cohort.status}
            </Badge>
          )}
        </Group>

        {/* Date range */}
        <Text size="sm" c="dimmed">
          {cohort.start_date && cohort.end_date
            ? formatDateOnlyRange(cohort.start_date, cohort.end_date)
            : 'Dates not set'}
        </Text>

        {/* Progress */}
        <Stack gap={4}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
            Progress
          </Text>
          <Group gap="xs">
            <Progress
              value={sessionProgress}
              size="sm"
              color="medleyGreen"
              style={{ width: 60 }}
            />
            <Text size="sm" fw={500}>
              {sessionProgress}%
            </Text>
          </Group>
        </Stack>

        {/* Stats row */}
        <Group gap="xl">
          <Group gap="xs">
            <ThemeIcon size="sm" variant="light" color="gray">
              <Users size={12} />
            </ThemeIcon>
            <Text size="sm" c="dimmed">
              {cohort.coach_group_count} group{cohort.coach_group_count !== 1 ? 's' : ''}
            </Text>
          </Group>
          <Group gap="xs">
            <ThemeIcon size="sm" variant="light" color="gray">
              <Briefcase size={12} />
            </ThemeIcon>
            <Text size="sm" c="dimmed">
              {cohort.sessions_completed}/{cohort.session_count} sessions
            </Text>
          </Group>
        </Group>
      </Stack>
      {/* MED-1394: outside the faded Stack, so the message keeps full contrast. */}
      {isAwaitingContract && (
        <Text size="sm" mt="md" className={classes.contractNote}>
          Your contract for this cohort is not signed yet. Contact the Medley team to finish it.
        </Text>
      )}
    </Card>
  );
}

// Empty state component
function EmptyState() {
  return (
    <Card withBorder padding="xl" radius="md">
      <Stack align="center" gap="md" py="xl">
        <ThemeIcon size={64} radius="xl" variant="light" color="gray">
          <Briefcase size={32} />
        </ThemeIcon>
        <Title order={3} ta="center" c="dimmed">
          No Cohorts Assigned
        </Title>
        <Text size="sm" c="dimmed" ta="center" maw={400}>
          You haven't been assigned to any cohorts yet. Once you're matched to
          groups, your cohorts will appear here.
        </Text>
      </Stack>
    </Card>
  );
}

export function CoachCohortsPage() {
  const navigate = useNavigate();
  const [cohorts, setCohorts] = useState<CoachCohort[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCohorts = async () => {
      try {
        const data = await coachPortalApi.getCohorts();
        setCohorts(data);
      } catch (error) {
        console.error('Failed to fetch cohorts:', error);
        notifications.show({
          title: 'Error',
          message: 'Failed to load your cohorts. Please try again.',
          color: 'red',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCohorts();
  }, []);

  const handleCohortClick = (cohortId: string) => {
    navigate(`/coach/cohorts/${cohortId}`);
  };

  if (loading) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <Container size="lg" py="xl">
      <Title order={2} mb="xl">
        My Cohorts
      </Title>

      {cohorts.length === 0 ? (
        <EmptyState />
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
          {cohorts.map(cohort => (
            <CohortCard
              key={cohort.id}
              cohort={cohort}
              onClick={() => handleCohortClick(cohort.id)}
            />
          ))}
        </SimpleGrid>
      )}
    </Container>
  );
}

export default CoachCohortsPage;
